/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DANGER,
  BUTTON_THEME_DEFAULT,
  Button,
  ErrorMessage,
  TOAST_TYPE_SUCCESS,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { CDP_STRINGS } from '../../../../../Constants/Strings.constants';
import { InsightsServiceDeleteInsightDto, getInsightsService } from '../../../../../Services/insights';
import { SegmentationServiceDeleteEventDto, getSegmentationService } from '../../../../../Services/segmentation';
import { EVENTS_DATA_IDS } from '../../../Events.constants';
import { EVENTS_LIST_SOURCE_WEB_BEHAVIOR } from '../EventsList.constants';
import { DeleteEventDialogProps } from './DeleteEventDialog.props';

/**
 * The confirmation dialog for deleting a event.
 */
export const DeleteEventDialog = (props: DeleteEventDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const { clearCache, close, name, source, id, setAfterClose, setButtonKeys, setButtons } = props;

  // Services
  const insightsService = getInsightsService();
  const segmentationService = getSegmentationService();

  const { updateActiveToast } = useToastStore();

  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  // Labels
  const labels = useMemo(() => {
    return {
      cancelButton: t(CDP_STRINGS.cancel),
      deleteButton: t(CDP_STRINGS.deleteEventDialogDeleteButton),
      deleteError: t(CDP_STRINGS.deleteEventDialogErrorMessage),
      dialogContent: t(CDP_STRINGS.deleteEventDialogMessage),
      toastSuccess1: t(CDP_STRINGS.toastSuccessDeleteMessage),
      toastSuccess2: t(CDP_STRINGS.toastSuccessDeleteMessageEnd),
      toastTitle: t(CDP_STRINGS.toastSuccessDeleteLabel),
    };
  }, [t]);

  const customClose = useCallback(() => {
    // Refresh event list on dialog close
    clearCache(new Date().toISOString());
  }, [clearCache]);

  const submitHandler = useCallback((): void => {
    if (source === EVENTS_LIST_SOURCE_WEB_BEHAVIOR) {
      // Prepare the DTO
      const dto: SegmentationServiceDeleteEventDto = {
        id,
      };

      lastValueFrom(segmentationService.deleteEvent(dto))
        .then(() => {
          const successToastProps: ToastProps = {
            showToast: true,
            stateHandler: useToastStore,
            title: labels.toastTitle,
            toastContent: (
              <span>
                {labels.toastSuccess1} <em>{name}</em> {labels.toastSuccess2}
              </span>
            ),
            toastType: TOAST_TYPE_SUCCESS,
          };
          close();
          updateActiveToast(<Toast {...successToastProps} data-id={EVENTS_DATA_IDS.toastSuccessDeleteEvent} />);
          return;
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else {
      // Prepare the DTO
      const dto: InsightsServiceDeleteInsightDto = {
        insightId: id,
      };

      lastValueFrom(insightsService.deleteInsight(dto))
        .then(() => {
          const successToastProps: ToastProps = {
            showToast: true,
            stateHandler: useToastStore,
            title: labels.toastTitle,
            toastContent: (
              <span>
                {labels.toastSuccess1} <em>{name}</em> {labels.toastSuccess2}
              </span>
            ),
            toastType: TOAST_TYPE_SUCCESS,
          };
          close();
          updateActiveToast(<Toast {...successToastProps} data-id={EVENTS_DATA_IDS.toastSuccessDeleteEvent} />);
          return;
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    }
  }, [
    close,
    id,
    insightsService,
    labels.toastSuccess1,
    labels.toastSuccess2,
    labels.toastTitle,
    name,
    segmentationService,
    source,
    updateActiveToast,
  ]);

  useEffect(() => {
    // Cancel button
    const cancelButtonKey = `cancel`;
    const cancelButton = (
      <Button
        data-id={EVENTS_DATA_IDS.deleteEventDialogButtonCancel}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancelButton}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    // Delete button
    const deleteButtonKey = `delete`;
    const deleteButton = (
      <Button
        data-id={EVENTS_DATA_IDS.deleteEventDialogButtonDelete}
        isLoading={isLoading}
        onClick={(): void => {
          setLoading(true);
          setError(false);
          submitHandler();
        }}
        text={labels.deleteButton}
        theme={BUTTON_THEME_DANGER}
      />
    );

    // Refresh event list after deletion
    setAfterClose && setAfterClose(() => customClose);

    if (isLoading) {
      setButtonKeys([deleteButtonKey]);
      setButtons([deleteButton]);
    } else {
      setButtonKeys([cancelButtonKey, deleteButtonKey]);
      setButtons([cancelButton, deleteButton]);
    }
  }, [close, customClose, isLoading, labels, setAfterClose, setButtonKeys, setButtons, setLoading, submitHandler]);

  return (
    <>
      <div>{labels.dialogContent}</div>
      {error && (
        <div className="sds-form-error" data-id={EVENTS_DATA_IDS.deleteEventDialogError}>
          <ErrorMessage message={labels.deleteError} />
        </div>
      )}
    </>
  );
};
